import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Star, User, Phone, Calendar, Clock, Sparkles, 
  CheckCircle, AlertCircle, ThumbsUp, ThumbsDown,
  Send, Filter, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ value, onChange, label, required }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="space-y-1">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                star <= (hover || value)
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        {value > 0 && (
          <span className="ml-2 text-sm text-muted-foreground self-center">
            {value}/5
          </span>
        )}
      </div>
    </div>
  );
};

const RateYourCustomers = ({ therapistId, therapistName, propertyId }) => {
  const [unratedServices, setUnratedServices] = useState([]);
  const [givenRatings, setGivenRatings] = useState([]);
  const [receivedFeedback, setReceivedFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  
  // Rating dialog state
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ratingForm, setRatingForm] = useState({
    overall_rating: 0,
    behavior_rating: 0,
    punctuality_rating: 0,
    hygiene_rating: 0,
    would_serve_again: null,
    comment: ''
  });

  useEffect(() => {
    if (therapistId && propertyId) {
      fetchData();
    }
  }, [therapistId, propertyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [unratedRes, givenRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/customer-ratings/property/${propertyId}/unrated-services?therapist_id=${therapistId}`, { headers }),
        axios.get(`${API}/customer-ratings/therapist/${therapistId}`, { headers }),
        axios.get(`${API}/therapist-feedback/${therapistId}`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setUnratedServices(unratedRes.data);
      setGivenRatings(givenRes.data);
      setReceivedFeedback(feedbackRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load rating data');
    } finally {
      setLoading(false);
    }
  };

  const openRatingDialog = (service) => {
    setSelectedService(service);
    setRatingForm({
      overall_rating: 0,
      behavior_rating: 0,
      punctuality_rating: 0,
      hygiene_rating: 0,
      would_serve_again: null,
      comment: ''
    });
    setRatingDialogOpen(true);
  };

  const submitRating = async () => {
    // Validate required fields
    if (!ratingForm.overall_rating || !ratingForm.behavior_rating || 
        !ratingForm.punctuality_rating || !ratingForm.hygiene_rating ||
        ratingForm.would_serve_again === null) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/customer-ratings`, {
        customer_phone: selectedService.customer_phone,
        customer_name: selectedService.customer_name,
        therapist_id: therapistId,
        property_id: propertyId,
        booking_id: selectedService.booking_id,
        overall_rating: ratingForm.overall_rating,
        behavior_rating: ratingForm.behavior_rating,
        punctuality_rating: ratingForm.punctuality_rating,
        hygiene_rating: ratingForm.hygiene_rating,
        would_serve_again: ratingForm.would_serve_again,
        comment: ratingForm.comment || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Customer rating submitted successfully');
      setRatingDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast.error('Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rate-customers-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-serif font-semibold">Rate Your Customers</h2>
          <p className="text-sm text-muted-foreground">
            Provide feedback on customers you've served
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Selector */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'pending' 
              ? 'border-amber-600 text-amber-600' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="pending-ratings-tab"
        >
          To Rate ({unratedServices.length})
        </button>
        <button
          onClick={() => setActiveTab('given')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'given' 
              ? 'border-amber-600 text-amber-600' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="given-ratings-tab"
        >
          Given ({givenRatings.length})
        </button>
        <button
          onClick={() => setActiveTab('received')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'received' 
              ? 'border-amber-600 text-amber-600' 
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="received-ratings-tab"
        >
          Received
        </button>
      </div>

      {/* Pending Ratings */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          {unratedServices.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p className="text-muted-foreground">No pending customer ratings!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You've rated all your recent customers.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {unratedServices.map((service) => (
                <Card key={service.booking_id} className="hover:border-amber-300 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-amber-700" />
                        </div>
                        <div>
                          <p className="font-medium">{service.customer_name}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Phone className="w-3 h-3" />
                            {service.customer_phone}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">{service.therapy_type}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                          <Calendar className="w-3 h-3" />
                          {formatDate(service.booking_date)}
                        </p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => openRatingDialog(service)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid={`rate-customer-${service.booking_id}`}
                      >
                        <Star className="w-4 h-4 mr-1" />
                        Rate
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Given Ratings */}
      {activeTab === 'given' && (
        <div className="space-y-4">
          {givenRatings.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <Star className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-muted-foreground">No ratings given yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Start rating customers after completing services.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {givenRatings.map((rating) => (
                <Card key={rating.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium">{rating.customer_name}</p>
                          <p className="text-sm text-muted-foreground">{rating.customer_phone}</p>
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= rating.overall_rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <Badge variant={rating.would_serve_again ? 'success' : 'destructive'} className="text-xs">
                          {rating.would_serve_again ? (
                            <><ThumbsUp className="w-3 h-3 mr-1" /> Would serve again</>
                          ) : (
                            <><ThumbsDown className="w-3 h-3 mr-1" /> Would not serve</>
                          )}
                        </Badge>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center p-2 bg-muted rounded">
                        <p className="text-muted-foreground">Behavior</p>
                        <p className="font-medium">{rating.behavior_rating}/5</p>
                      </div>
                      <div className="text-center p-2 bg-muted rounded">
                        <p className="text-muted-foreground">Punctuality</p>
                        <p className="font-medium">{rating.punctuality_rating}/5</p>
                      </div>
                      <div className="text-center p-2 bg-muted rounded">
                        <p className="text-muted-foreground">Hygiene</p>
                        <p className="font-medium">{rating.hygiene_rating}/5</p>
                      </div>
                    </div>
                    {rating.comment && (
                      <p className="mt-2 text-sm text-muted-foreground italic">
                        "{rating.comment}"
                      </p>
                    )}
                    <p className="mt-2 text-xs text-muted-foreground">
                      Rated on {formatDate(rating.created_at)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rating Dialog */}
      <Dialog open={ratingDialogOpen} onOpenChange={setRatingDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="customer-rating-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">Rate Customer</DialogTitle>
            <DialogDescription>
              Provide feedback for {selectedService?.customer_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Customer Info */}
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <User className="w-8 h-8 text-muted-foreground" />
              <div>
                <p className="font-medium">{selectedService?.customer_name}</p>
                <p className="text-sm text-muted-foreground">{selectedService?.therapy_type}</p>
              </div>
            </div>
            
            {/* Rating Fields */}
            <StarRating
              label="Overall Experience"
              value={ratingForm.overall_rating}
              onChange={(v) => setRatingForm({ ...ratingForm, overall_rating: v })}
              required
            />
            
            <StarRating
              label="Behavior & Attitude"
              value={ratingForm.behavior_rating}
              onChange={(v) => setRatingForm({ ...ratingForm, behavior_rating: v })}
              required
            />
            
            <StarRating
              label="Punctuality"
              value={ratingForm.punctuality_rating}
              onChange={(v) => setRatingForm({ ...ratingForm, punctuality_rating: v })}
              required
            />
            
            <StarRating
              label="Hygiene"
              value={ratingForm.hygiene_rating}
              onChange={(v) => setRatingForm({ ...ratingForm, hygiene_rating: v })}
              required
            />
            
            {/* Would Serve Again */}
            <div className="space-y-2">
              <Label>
                Would you serve this customer again? <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={ratingForm.would_serve_again === true ? 'default' : 'outline'}
                  className={ratingForm.would_serve_again === true ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setRatingForm({ ...ratingForm, would_serve_again: true })}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={ratingForm.would_serve_again === false ? 'default' : 'outline'}
                  className={ratingForm.would_serve_again === false ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setRatingForm({ ...ratingForm, would_serve_again: false })}
                >
                  <ThumbsDown className="w-4 h-4 mr-2" />
                  No
                </Button>
              </div>
            </div>
            
            {/* Comment */}
            <div className="space-y-2">
              <Label>Additional Comments (Optional)</Label>
              <Textarea
                placeholder="Any specific observations about this customer..."
                value={ratingForm.comment}
                onChange={(e) => setRatingForm({ ...ratingForm, comment: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRatingDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitRating}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-customer-rating-btn"
            >
              {submitting ? (
                <>Submitting...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Submit Rating</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RateYourCustomers;
